import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('input'); // input, success, error
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!email || code.length !== 6) {
      toast.error(language === 'ja' ? 'メールアドレスと6桁の認証コードを入力してください' : 'Please enter email and 6-digit code');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/users/verify-email`, { email, code });
      setStatus('success');
      setMessage(response.data.message);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || (language === 'ja' ? '認証に失敗しました' : 'Verification failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error(language === 'ja' ? 'メールアドレスを入力してください' : 'Please enter your email');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/api/users/resend-verification`, { email });
      toast.success(language === 'ja' ? '認証コードを再送信しました' : 'Verification code resent');
      setStatus('input');
      setMessage('');
      setCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ja' ? '送信に失敗しました' : 'Failed to send'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'success' ? (
          <>
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {language === 'ja' ? '認証完了！' : 'Verified!'}
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link to="/register?mode=login">
              <Button className="bg-[#00A651] hover:bg-[#008a43] w-full">
                {language === 'ja' ? 'ログインする' : 'Go to Login'}
              </Button>
            </Link>
          </>
        ) : (
          <>
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {language === 'ja' ? 'メールアドレスの認証' : 'Email Verification'}
            </h1>
            <p className="text-gray-600 mb-6">
              {language === 'ja' ? '登録時に送信された6桁の認証コードを入力してください。' : 'Enter the 6-digit code sent to your email.'}
            </p>

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-700 text-left">{message}</p>
              </div>
            )}

            <div className="space-y-4 text-left mb-6">
              <div>
                <Label className="text-sm">{language === 'ja' ? 'メールアドレス' : 'Email'}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">{language === 'ja' ? '認証コード（6桁）' : 'Verification Code (6 digits)'}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setStatus('input'); }}
                  placeholder="000000"
                  className="mt-1 text-center text-2xl tracking-[0.5em] font-bold h-14"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleVerify}
                className="w-full bg-[#00A651] hover:bg-[#008a43]"
                disabled={loading || code.length !== 6 || !email}
              >
                {loading ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> {language === 'ja' ? '確認中...' : 'Verifying...'}</>
                ) : (
                  language === 'ja' ? '認証する' : 'Verify'
                )}
              </Button>
              <Button onClick={handleResend} variant="outline" className="w-full" disabled={loading || !email}>
                <RefreshCw className="h-4 w-4 mr-2" /> {language === 'ja' ? '認証コードを再送信' : 'Resend Code'}
              </Button>
              <Link to="/register?mode=login" className="block">
                <Button variant="ghost" className="w-full text-[#00A651]">
                  {language === 'ja' ? 'ログインページへ' : 'Go to Login'}
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
